import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback
} from 'react';
import {
  clearAuthSession as clearAuthSessionUtil,
  isAuthenticated as isAuthenticatedUtil,
  saveAuthSession as saveAuthSessionUtil,
  AuthSession
} from 'features/auth/authSession';

interface AuthContextInterface {
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  login: (session: AuthSession) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextInterface | undefined>(undefined);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    isAuthenticatedUtil()
  );
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const login = useCallback((session: AuthSession) => {
    saveAuthSessionUtil(session);
    setIsLoggingOut(false);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    setIsLoggingOut(true);
    clearAuthSessionUtil();
    setIsAuthenticated(false);
  }, []);

  const value: AuthContextInterface = {
    isAuthenticated,
    isLoggingOut,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
